import { supabase } from '@/integrations/supabase/client'
import { getAuthHeaders } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_KEY

if (!supabaseUrl || !supabaseKey) {
	console.error('Missing environment variables:', {
		supabaseUrl,
		supabaseKey,
	})
	throw new Error('Missing Supabase environment variables')
}

// Export the singleton instance
export { supabase }

export interface BugReportData {
	widgetId: string
	teamId: string
	programId?: string
	description: string
	screenshot?: File | null
	reporterEmail?: string
	reporterName?: string
	externalUserId?: string
	userProperties?: Record<string, any>
}

export interface FeatureRequestData {
	widgetId: string
	teamId: string
	programId: string
	title: string
	description: string
	categoryId?: string
	submitterIdentifier?: string
	submitterEmail?: string
	submitterName?: string
	externalUserId?: string
	userProperties?: Record<string, any>
}

export interface ContactMessageData {
	widgetId: string
	teamId: string
	programId?: string
	description: string
	screenshot?: File | null
	reporterEmail?: string
	reporterName?: string
	externalUserId?: string
	userProperties?: Record<string, any>
}

export interface ReviewData {
	widgetId: string
	teamId: string
	programId: string
	reviewText: string
	score?: number
	reporterName?: string
	reporterEmail?: string
	reporterCompany?: string
	consentGiven: boolean
	clientUserId?: string
	externalUserId?: string
	userProperties?: Record<string, any>
	responseData?: {
		reviewText: string
		consentGiven: boolean
		customAnswers: {
			[key: string]: {
				questionText: string
				answer: string
				type: string
			}
		}
	}
}

export interface FeatureRequest {
	id: string
	title: string
	description: string
	status: string
	upvote_count: number
	created_at: string
	updated_at?: string
	team_id?: string
	program_id?: string
	widget_id?: string
	category_id?: string
	submitter_identifier?: string
	submitter_email?: string
	submitter_name?: string | null
	widget?: { name: string } | null
	program?: { name: string } | null
	public_response?: string
	is_publicly_visible?: boolean
}

export interface VerificationRequestData {
	widgetId: string
	programId: string
	email: string
	score: number
	reviewText: string
	name?: string
	socialLink?: string
}

export interface VerifiedReviewData {
	widgetId: string
	programId: string
	email: string
	verificationCode: string
	score?: number
	reviewText: string
	consentGiven: boolean
	name?: string
	socialLink?: string
	userMetadata?: Record<string, any>
	source?: string
}

/**
 * Gather complete user metadata for submissions
 * Captures page context, device info, and browser details
 */
function gatherUserMetadata() {
	return {
		pageUrl: window.location.href,
		pageTitle: document.title,
		userAgent: navigator.userAgent,
		screenWidth: window.screen.width,
		screenHeight: window.screen.height,
	}
}

export async function submitBugReport(data: BugReportData) {
	// Use VITE_API_URL from .env file, fallback to localhost:4000
	const backendUrl = getApiBaseUrl()
	let publicScreenshotUrl: string | null = null

	try {
		// Step 1: Upload screenshot via backend endpoint if provided
		if (data.screenshot) {
			const formData = new FormData()
			formData.append('screenshot', data.screenshot)
			// formData.append('teamId', data.teamId) // REMOVED teamId
			formData.append('widgetId', data.widgetId) // ADDED widgetId, as expected by backend

			const uploadResponse = await fetch(
				`${backendUrl}/api/bugs/upload-screenshot`,
				{
					method: 'POST',
					body: formData,
					// Don't set Content-Type header, browser does it correctly for FormData
				}
			)

			if (!uploadResponse.ok) {
				const errorBody = await uploadResponse.json().catch(() => ({}))
				console.error(
					'Screenshot Upload API Error:',
					uploadResponse.status,
					errorBody
				)
				throw new Error(
					`Screenshot Upload Failed: ${uploadResponse.status} - ${
						errorBody.error || uploadResponse.statusText
					}`
				)
			}

			const uploadResult = await uploadResponse.json()
			publicScreenshotUrl = uploadResult.screenshotUrl // Get URL from backend response

			if (!publicScreenshotUrl) {
				console.warn(
					'Backend uploaded screenshot but did not return a URL. Path:',
					uploadResult.path
				)
				// Decide if you want to proceed without a URL or throw an error
				// For now, we'll proceed, the backend will store null or the path
			}
		}

		// Step 2: Gather complete user metadata
		const userMetadata = gatherUserMetadata()

		// Step 3: Construct payload for the main bug report API
		const payload = {
			widgetId: data.widgetId,
			description: data.description,
			screenshotUrl: publicScreenshotUrl, // Send the URL received from the upload endpoint
			userMetadata: userMetadata,
			reporterName: data.reporterName,
			reporterEmail: data.reporterEmail,
			externalUserId: data.externalUserId,
			userProperties: data.userProperties,
		}

		// Step 4: Call the main backend API endpoint to create the bug report
		const reportResponse = await fetch(`${backendUrl}/api/bugs`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})

		if (!reportResponse.ok) {
			const errorBody = await reportResponse.json().catch(() => ({}))
			console.error(
				'Bug Report API Error Response:',
				reportResponse.status,
				errorBody
			)
			throw new Error(
				`API Error: ${reportResponse.status} ${reportResponse.statusText} - ${
					errorBody.error || 'Unknown error'
				}`
			)
		}

		// Bug report submitted successfully
	} catch (error) {
		console.error('Error in submitBugReport process:', error)
		// Re-throw the error so the UI can catch it and display a message
		throw error
	}
}

export async function submitFeatureRequest(data: FeatureRequestData) {
	const backendUrl = getApiBaseUrl()

	try {
		// Gather complete user metadata
		const userMetadata = gatherUserMetadata()

		const payload = {
			widgetId: data.widgetId,
			teamId: data.teamId,
			programId: data.programId,
			title: data.title,
			description: data.description,
			categoryId: data.categoryId,
			submitterIdentifier: data.submitterIdentifier,
			submitterName: data.submitterName,
			submitterEmail: data.submitterEmail,
			externalUserId: data.externalUserId,
			userProperties: data.userProperties,
			userMetadata: userMetadata,
		}

		// This is a public endpoint for submitting new feature requests
		const response = await fetch(`${backendUrl}/api/public/features`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			console.error(
				'Feature Request API Error Response:',
				response.status,
				errorBody
			)
			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				}`
			)
		}

		return await response.json()
	} catch (error) {
		console.error('Error in submitFeatureRequest process:', error)
		throw error
	}
}

export async function submitContactMessage(data: ContactMessageData) {
	const backendUrl = getApiBaseUrl()
	let publicScreenshotUrl: string | null = null

	try {
		if (data.screenshot) {
			const formData = new FormData()
			formData.append('screenshot', data.screenshot)
			formData.append('widgetId', data.widgetId)

			const uploadResponse = await fetch(
				`${backendUrl}/api/contacts/upload-screenshot`,
				{
					method: 'POST',
					body: formData,
				}
			)

			if (!uploadResponse.ok) {
				const errorBody = await uploadResponse.json().catch(() => ({}))
				throw new Error(
					`Screenshot Upload Failed: ${uploadResponse.status} - ${
						errorBody.error || uploadResponse.statusText
					}`
				)
			}

			const uploadResult = await uploadResponse.json()
			publicScreenshotUrl = uploadResult.screenshotUrl || null
		}

		const payload = {
			widgetId: data.widgetId,
			description: data.description,
			screenshotUrl: publicScreenshotUrl,
			userMetadata: gatherUserMetadata(),
			reporterName: data.reporterName,
			reporterEmail: data.reporterEmail,
			externalUserId: data.externalUserId,
			userProperties: data.userProperties,
		}

		const response = await fetch(`${backendUrl}/api/contacts`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				}`
			)
		}

		return await response.json()
	} catch (error) {
		console.error('Error in submitContactMessage process:', error)
		throw error
	}
}

export async function fetchFeatureRequests(
	widgetId: string,
	programId?: string
): Promise<FeatureRequest[]> {
	const backendUrl = getApiBaseUrl()

	try {
		const queryParams = new URLSearchParams()
		if (widgetId) queryParams.append('widgetId', widgetId)
		if (programId) queryParams.append('programId', programId)

		// Use public endpoint for widget previews (no authentication required)
		const url = `${backendUrl}/api/public/features?${queryParams.toString()}`

		const response = await fetch(url)

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			console.error(
				'API Error fetching features:',
				response.status,
				response.statusText,
				errorBody
			)
			// Add more context to the error
			throw new Error(
				`API Error (${response.status}): ${
					errorBody.error || response.statusText || 'Failed to fetch features'
				}`
			)
		}

		const responseData = await response.json()

		// Handle both formats: { data: [] } or direct array
		let features = []

		if (Array.isArray(responseData)) {
			// Direct array response
			features = responseData
		} else if (
			responseData &&
			responseData.data &&
			Array.isArray(responseData.data)
		) {
			// Wrapped response format
			features = responseData.data
		} else {
			console.warn(
				'[fetchFeatureRequests] Unexpected response format:',
				responseData
			)
			features = []
		}


		return features
	} catch (error) {
		console.error('Error fetching feature requests:', error)
		throw error
	}
}

export async function upvoteFeature(
	featureId: string
): Promise<{ success: boolean; feature?: FeatureRequest; upvoted: boolean }> {
	if (
		!featureId ||
		featureId === 'undefined' ||
		typeof featureId !== 'string'
	) {
		console.error('[upvoteFeature] Invalid featureId provided:', featureId)
		return { success: false, upvoted: false }
	}

	const backendUrl = getApiBaseUrl()
	let clientId = localStorage.getItem('feedback_client_id')

	if (!clientId) {
		clientId = generateClientId()
		localStorage.setItem('feedback_client_id', clientId)
	}

	try {
		// Use the new anonymous upvote endpoint for widget previews
		const url = `${backendUrl}/api/public/features/${featureId}/upvote/anonymous`

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ clientId }),
		})

		const result = await response.json()

		if (!response.ok) {
			if (response.status === 409) {
				return { success: false, upvoted: true }
			}
			console.error('[upvoteFeature] API Error:', result)
			throw new Error(result.error || 'Failed to upvote feature')
		}

		return {
			success: result.success,
			upvoted: result.upvoted,
			// Map the response to match expected interface
			feature: result.upvoteCount
				? ({ upvote_count: result.upvoteCount } as FeatureRequest)
				: undefined,
		}
	} catch (error) {
		console.error('[upvoteFeature] Error upvoting feature:', error)
		throw error
	}
}

export async function checkUpvoteStatus(featureId: string): Promise<boolean> {
	if (!featureId || featureId === 'undefined') {
		console.error('[checkUpvoteStatus] Invalid featureId provided:', featureId)
		return false
	}

	const backendUrl = getApiBaseUrl()
	let clientId = localStorage.getItem('feedback_client_id')

	if (!clientId) {
		clientId = generateClientId()
		localStorage.setItem('feedback_client_id', clientId)
	}

	try {
		const url = `${backendUrl}/api/public/features/${featureId}/upvote/status?clientId=${clientId}`

		// This is a public endpoint for checking upvote status
		const response = await fetch(url)

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			console.error('[checkUpvoteStatus] API Error:', {
				status: response.status,
				statusText: response.statusText,
				errorBody,
				featureId,
				url,
			})
			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				}`
			)
		}

		const result = await response.json()
		return result.hasUpvoted
	} catch (error) {
		console.error('[checkUpvoteStatus] Error checking upvote status:', error)
		return false // Default to not upvoted on error
	}
}

// Protected (Dashboard) upvote functions
export async function upvoteFeatureProtected(
	featureId: string
): Promise<{ success: boolean; feature?: FeatureRequest; upvoted: boolean }> {
	const backendUrl = getApiBaseUrl()

	try {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${backendUrl}/api/features/protected/${featureId}/upvote`,
			{
				method: 'POST',
				headers: {
					...headers,
					'Content-Type': 'application/json',
				},
			}
		)

		const result = await response.json()

		if (!response.ok) {
			if (response.status === 409) {
				return { success: false, upvoted: true }
			}
			throw new Error(result.error || 'Failed to upvote feature')
		}

		return result
	} catch (error) {
		console.error('Error upvoting feature:', error)
		throw error
	}
}

export async function checkUpvoteStatusProtected(
	featureId: string
): Promise<boolean> {
	const backendUrl = getApiBaseUrl()

	try {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${backendUrl}/api/features/protected/${featureId}/upvote/status`,
			{
				headers: headers,
			}
		)

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				}`
			)
		}

		const result = await response.json()
		return result.upvoted
	} catch (error) {
		console.error('Error checking upvote status:', error)
		return false // Default to not upvoted on error
	}
}

// Generate a unique client ID
function generateClientId(): string {
	// Create a UUID v4 (random)
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0,
			v = c === 'x' ? r : (r & 0x3) | 0x8
		return v.toString(16)
	})
}

export async function submitReview(data: ReviewData) {
	// Use VITE_API_URL from .env file, fallback to localhost:4000
	const backendUrl = getApiBaseUrl()

	try {
		// Gather complete user metadata
		const userMetadata = gatherUserMetadata()

		// Construct payload for the review API, mapping to backend field names
		const payload = {
			widgetId: data.widgetId,
			teamId: data.teamId,
			programId: data.programId,
			score: data.score,
			reviewText: data.reviewText, // Ensure this maps to the field expected by PublicCreateReviewSchema
			reporterName: data.reporterName,
			reporterEmail: data.reporterEmail,
			reporterCompany: data.reporterCompany,
			consent_given: data.consentGiven, // Match the backend field name
			externalUserId: data.externalUserId,
			userProperties: data.userProperties,
			userMetadata: userMetadata,
			// Include responseData for custom fields if needed by frontend
			responseData: data.responseData,
		}

		// Call the backend API endpoint to create the review
		const response = await fetch(`${backendUrl}/api/reviews`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			console.error('Review API Error Response:', response.status, errorBody)
			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				}`
			)
		}

		return await response.json()
	} catch (error) {
		console.error('Error in submitReview process:', error)
		// Re-throw the error so the UI can catch it and display a message
		throw error
	}
}

export async function requestVerificationCode(
	data: VerificationRequestData,
	resend: boolean = false
): Promise<{ success: boolean; canResend?: boolean; message?: string }> {
	const backendUrl = getApiBaseUrl()

	try {
		const response = await fetch(
			`${backendUrl}/api/reviews/request-verification${
				resend ? '?resend=true' : ''
			}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(data),
			}
		)

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			console.error(
				'Verification Request API Error Response:',
				response.status,
				errorBody
			)

			// If there's already a code sent but we can resend, return that info
			if (response.status === 429 && errorBody.canResend) {
				return {
					success: false,
					canResend: true,
				}
			}

			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				} (${errorBody.code || 'unknown_code'})`
			)
		}

		const responseData = await response.json()
		return {
			success: responseData.success !== undefined ? responseData.success : true, // Default to true if response.ok and no explicit success field
			canResend: responseData.canResend,
			message: responseData.message, // Pass through message from backend
		}
	} catch (error: any) {
		console.error('Error requesting verification code:', error)
		throw error
	}
}

export async function submitVerifiedReview(
	data: VerifiedReviewData
): Promise<{ id: string }> {
	const backendUrl = getApiBaseUrl()

	try {
		// Gather complete user metadata
		const userMetadata = gatherUserMetadata()

		// Construct payload for the verified review API
		const payload = {
			...data,
			userMetadata: data.userMetadata || userMetadata,
		}

		// Call the backend API endpoint to submit the verified review
		const response = await fetch(`${backendUrl}/api/reviews/submit-verified`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const errorBody = await response.json().catch(() => ({}))
			console.error(
				'Verified Review API Error Response:',
				response.status,
				errorBody
			)
			throw new Error(
				`API Error: ${response.status} ${response.statusText} - ${
					errorBody.error || 'Unknown error'
				} (${errorBody.code || 'unknown_code'})`
			)
		}

		return await response.json()
	} catch (error) {
		console.error('Error in submitVerifiedReview process:', error)
		throw error
	}
}
